import { UserRoleEnum } from '@app/types';
import {
  CustomLinkItem,
  SystemDropdownItem,
  SystemLinkItem,
} from '@app/gen/side_menu';
import { NavigationItem } from '@app/router';
import { Resources } from '@app/ability';
import { ModulesNames, modules } from '@app/modules';

import { MenuItem } from './sidebarMapper';
import { sidebarCustomIcons, sidebarIcons } from './sidebarIcons';

const findModuleNick = (
  cleanNick: string,
  role?: UserRoleEnum | 'general',
  isDropdown = false,
) => {
  if (cleanNick === 'company_settings') {
    return {
      moduleNick: 'company' as ModulesNames,
      role,
      isDropdown,
    };
  }
  if (modules[cleanNick as ModulesNames]) {
    return {
      moduleNick: cleanNick as ModulesNames,
      role,
      isDropdown,
    };
  }

  if (cleanNick.startsWith('settings_')) {
    const withoutSettings = cleanNick.replace('settings_', '');
    if (modules[withoutSettings as ModulesNames]) {
      return {
        moduleNick: withoutSettings as ModulesNames,
        role,
        isDropdown,
      };
    }
  }

  if (cleanNick.includes('_')) {
    const parts = cleanNick.split('_');
    const lastPart = parts[parts.length - 1] as ModulesNames;
    if (modules[lastPart]) {
      return {
        moduleNick: lastPart,
        role,
        isDropdown,
      };
    }
  }

  return {
    moduleNick: cleanNick as ModulesNames,
    role,
    isDropdown,
  };
};

export const parseNick = (
  nick: string,
): {
  moduleNick: ModulesNames;
  role?: UserRoleEnum | 'general';
  isDropdown: boolean;
} => {
  const isDropdown = nick.startsWith('dropdown_');
  const cleanNick = isDropdown ? nick.replace('dropdown_', '') : nick;

  let role: UserRoleEnum | 'general' | undefined;
  let finalNick = cleanNick;

  if (!cleanNick.startsWith('company_') && !cleanNick.startsWith('employee_')) {
    return findModuleNick(finalNick, 'general', isDropdown);
  }
  if (nick === 'company_settings' || nick === 'company_events') {
    return findModuleNick(nick, UserRoleEnum.Company, isDropdown);
  }
  if (cleanNick.startsWith('company_')) {
    role = UserRoleEnum.Company;
    finalNick = cleanNick.replace('company_', '');
  }
  if (cleanNick.startsWith('employee_')) {
    role = UserRoleEnum.Employee;
    finalNick = cleanNick.replace('employee_', '');
  }

  return findModuleNick(finalNick, role, isDropdown);
};

export const customLinkToNavigationItem = (
  item: CustomLinkItem,
): NavigationItem => {
  const { link, icon, name } = item;
  const icons = sidebarCustomIcons();
  const sidebarIcon = icon ? icons[icon] : icons.globe;

  const basename = item.name.toLowerCase().split(' ').join('-');
  const aqaClass = `aqa_custom_${item.name.toLowerCase().split(' ').join('_')}`;

  return {
    href: link,
    icon: sidebarIcon,
    resource: 'all' as Resources,
    basename,
    aqaClass,
    title: name,
  };
};

const addRolePrefixToUrl = (
  url: string,
  role: UserRoleEnum | 'general',
): string => {
  const hasCompanyPrefix = url.startsWith('/company/');
  const hasEmployeePrefix = url.startsWith('/employee/');

  if (hasCompanyPrefix || hasEmployeePrefix || role === 'general') {
    return url;
  }

  return role === UserRoleEnum.Company ? `/company${url}` : `/employee${url}`;
};

export const systemLinkToNavigationItem = (
  item: SystemLinkItem,
): NavigationItem => {
  const { nick } = item;
  const { moduleNick, role } = parseNick(nick);
  const module = modules[moduleNick];
  const IconComponent = sidebarIcons()[nick];

  const noRolePrefixModules: ModulesNames[] = ['personal', 'main', 'news'];

  let href = module?.link ?? '';

  if (role && href && !noRolePrefixModules.includes(moduleNick)) {
    href = addRolePrefixToUrl(href, role);
  }

  return {
    href,
    icon: IconComponent,
    nick: nick,
    resource: module?.resource || ('all' as Resources),
    parentResource: module?.parentResource,
    basename: module?.basename || '',
    aqaClass: `aqa_${nick}`,
    role,
  };
};

export const systemDropdownToNavigationItem = (
  item: SystemDropdownItem,
): NavigationItem => {
  const { children, nick } = item;
  const { moduleNick, role } = parseNick(item.nick);
  const module = modules[moduleNick];

  const IconComponent = sidebarIcons()[nick];

  const itemChildren = children.map((child) => {
    if (child.type === 'link_item') {
      return systemLinkToNavigationItem(child);
    } else {
      return customLinkToNavigationItem(child);
    }
  });

  return {
    href: module?.link ?? '/dropdown',
    icon: IconComponent,
    resource: module?.resource || ('all' as Resources),
    parentResource: module?.parentResource,
    basename: module?.basename || item.nick,
    aqaClass: `aqa_${item.nick}`,
    nick: item.nick,
    role,
    submenu: itemChildren.map((childItem) => ({
      ...childItem,
      nick: childItem.nick,
      parentResource: childItem.parentResource,
    })),
  };
};

export const transformItemsToNavigation = (
  items: MenuItem[],
): NavigationItem[] => {
  return items.map((item) => {
    switch (item.type) {
      case 'custom_link_item':
        return customLinkToNavigationItem(item);

      case 'link_item':
        return systemLinkToNavigationItem(item);

      case 'dropdown_item':
        return systemDropdownToNavigationItem(item);

      default:
        return {
          href: '/',
          icon: sidebarCustomIcons().globe,
          resource: 'all' as Resources,
          basename: 'unknown',
          aqaClass: 'aqa_unknown',
        };
    }
  });
};
